"use client";

import React, { useState, useEffect } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const stages = [{
  title: "Prototype Stage",
  titleZh: "原型手辦階段",
  description: "Professional R&D team for prototype making, CAD & 3D drawing.",
  descriptionZh: "專業研發團隊提供手辦製作、CAD 及 3D 繪圖服務。",
  image: {
    src: "https://joint-gopher.10web.cloud/wp-content/uploads/2026/07/11.png?v=l191wm4nika",
    srcset: "https://joint-gopher.10web.cloud/wp-content/uploads/2026/07/11.png?v=x6itcwv3r9l-wvc-srcset",
    sizes: "https://joint-gopher.10web.cloud/wp-content/uploads/2026/07/11.png?v=2wf4zi3ov54-wvc-sizes",
    alt: "Prototype Stage Illustration",
    altZh: "原型手辦階段插圖"
  },
  isHigh: true,
  color: "primary"
}, {
  title: "Tooling Stage",
  titleZh: "模具開發階段",
  description: "High precision tooling shop to provide you with high-quality tooling with quick turnaround.",
  descriptionZh: "高精密模具車間為您提供快速交付的高品質模具製造。",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707169/img/tooling_stage.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707169/img/tooling_stage.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1707169/img/tooling_stage.png-wvc-sizes",
    alt: "Tooling Stage Illustration",
    altZh: "模具開發階段插圖"
  },
  isHigh: false,
  color: "foreground"
}, {
  title: "Engineer Pilot Stage",
  titleZh: "工程試產階段",
  description: "Product Engineer and PI technologist will provide useful debug recommendation during Engineer Pilot Stage.",
  descriptionZh: "產品工程師及 PI 技術專家於工程試產期間提供實用的調試與優化建議。",
  image: {
    src: "https://joint-gopher.10web.cloud/wp-content/uploads/2026/07/12.png?v=p7euswmozo",
    srcset: "https://joint-gopher.10web.cloud/wp-content/uploads/2026/07/12.png?v=sne3pv3mp4-wvc-srcset",
    sizes: "https://joint-gopher.10web.cloud/wp-content/uploads/2026/07/12.png?v=j7d4tpbj5o-wvc-sizes",
    alt: "Engineer Pilot Stage Illustration",
    altZh: "工程試產階段插圖"
  },
  isHigh: true,
  color: "primary"
}, {
  title: "Production Stage",
  titleZh: "量產製造階段",
  description: "Production Engineer and QA technologist can on-site or live-chat! to review production line and provide improvement solution.",
  descriptionZh: "生產工程師與品控技術專家可進行現場或即時線上審查產線，並提供改善方案。",
  image: {
    src: "https://joint-gopher.10web.cloud/wp-content/uploads/2026/06/production_slide23_photo_2.png?v=qrpdut68f7",
    srcset: "https://joint-gopher.10web.cloud/wp-content/uploads/2026/06/production_slide23_photo_2.png?v=m6hswl0ark-wvc-srcset",
    sizes: "https://joint-gopher.10web.cloud/wp-content/uploads/2026/06/production_slide23_photo_2.png?v=6jj8824kj8o-wvc-sizes",
    alt: "Production Stage Illustration",
    altZh: "量產製造階段插圖"
  },
  isHigh: false,
  color: "foreground"
}, {
  title: "Compliance Testing Stage",
  titleZh: "合規檢測階段",
  description: "QA technologist and CS can provide cost and saving solution for suitable laboratory for compliance testing.",
  descriptionZh: "品控專家與客戶服務團隊為合規檢測物色合適實驗室，提供高效節省成本的解決方案。",
  image: {
    src: "https://joint-gopher.10web.cloud/wp-content/uploads/2026/07/ISSO-Logo.png?v=ypjk5b34d3c",
    srcset: "https://joint-gopher.10web.cloud/wp-content/uploads/2026/07/ISSO-Logo.png?v=ptfc2yrlhre-wvc-srcset",
    sizes: "https://joint-gopher.10web.cloud/wp-content/uploads/2026/07/ISSO-Logo.png?v=ooivo4nktrl-wvc-sizes",
    alt: "Compliance Testing Stage Illustration",
    altZh: "合規檢測階段插圖"
  },
  isHigh: true,
  color: "primary"
}];
function StageItem({
  stage,
  index,
  isLast,
  isZh
}: {
  stage: typeof stages[0];
  index: number;
  isLast: boolean;
  isZh: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={cn("relative flex flex-col items-center text-center transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", stage.isHigh ? "md:mt-0" : "md:mt-[180px]")} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <div className="h-32 w-32 mb-6 relative z-10 bg-background rounded-xl p-2 shadow-sm border border-border/50 flex items-center justify-center">
        <img src={stage.image.src} data-wvc-srcset={stage.image.srcset} data-wvc-sizes={stage.image.sizes} alt={isZh ? stage.image.altZh : stage.image.alt} className="object-contain w-full h-full" loading="lazy" />
      </div>

      <div className="w-full flex justify-center relative mb-6">
        <div className={cn("w-6 h-6 rounded-full z-20 relative shadow-md ring-4 ring-background", stage.color === "primary" ? "bg-primary" : "bg-foreground")} />

        {!isLast && <svg className={cn("absolute left-1/2 w-[calc(100%+2rem)] h-[180px] overflow-visible pointer-events-none hidden md:block z-0", stage.isHigh ? "top-1/2" : "bottom-1/2")}>
            <line x1="0" y1={stage.isHigh ? "0" : "100%"} x2="100%" y2={stage.isHigh ? "100%" : "0"} className={cn("stroke-[3px]", stage.color === "primary" ? "stroke-primary" : "stroke-foreground")} markerEnd={`url(#arrow-${stage.color})`} />
          </svg>}
      </div>

      <div className="max-w-[250px] z-10 bg-background/80 backdrop-blur-sm p-2 rounded-lg">
        <h3 className={cn("font-default font-bold text-lg mb-3", stage.color === "primary" ? "text-primary" : "text-foreground")}>
          {isZh ? stage.titleZh : stage.title}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {isZh ? stage.descriptionZh : stage.description}
        </p>
      </div>
    </div>;
}
export default function ProductionProcessRoadmap() {
  const [isZh, setIsZh] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsZh(localStorage.getItem("elite-language") === "zh-Hant");
    }
  }, []);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section id="productionprocessroadmap" className="bg-background py-20 md:py-32 overflow-hidden relative">
      <svg className="w-0 h-0 absolute">
        <defs>
          <marker id="arrow-primary" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" className="fill-primary" />
          </marker>
          <marker id="arrow-foreground" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" className="fill-foreground" />
          </marker>
        </defs>
      </svg>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={cn("text-center mb-16 md:mb-24 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="font-default text-4xl md:text-5xl font-bold text-foreground">
            {isZh ? "生產流程" : "Production"}
          </h2>
        </div>

        <div className="relative">
          <div className="absolute left-1/2 top-[100px] bottom-[100px] w-0 border-l-2 border-dashed border-border -translate-x-1/2 md:hidden z-0" />

          <div className="grid grid-cols-1 md:grid-cols-5 gap-12 md:gap-8 relative z-10">
            {stages.map((stage, index) => <StageItem key={index} stage={stage} index={index} isLast={index === stages.length - 1} isZh={isZh} />)}
          </div>
        </div>
      </div>
    </section>;
}